import React, { FunctionComponent, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MenuItem, ROOT_PRODUCTS_KEY, ROOT_THEMATICS_KEY, sortItems, SortType } from '../MenuUtils';
import { MenuListItem } from '../components/MenuListItem';
import { DisplayMode } from './MenuDesktopContent';
import { MenuLink } from '../components/MenuLink';
import { MenuSorting } from '../components/MenuSorting';
import { MenuSeeAllOption } from '../components/MenuSeeAllOption';
import { MenuThematicsOptions } from '../components/MenuThematicsOptions';

export interface FirstPanelProps {
  category: MenuItem;
  menuSelectedPath: MenuItem[];
  setMenuSelectedPath: (menuItems: MenuItem[]) => void;
  selected: boolean;
  productsDisplayMode: DisplayMode;
  setProductsDisplayMode: (displayMode: DisplayMode) => void;
  sorting: SortType;
  setSorting: (sortType: SortType) => void;
  thematicsFilter: string;
  setThematicsFilter: (filter: string) => void;
  componentRef: React.RefObject<HTMLDivElement>;
  closeMenu: () => void;
  onClick: (label: string) => void;
}

export const MenuFirstPanelCategory: FunctionComponent<FirstPanelProps> = props => {
  const {
    category,
    menuSelectedPath,
    setMenuSelectedPath,
    setProductsDisplayMode,
    productsDisplayMode,
    setSorting,
    sorting,
    setThematicsFilter,
    thematicsFilter,
    selected,
    componentRef,
    closeMenu,
    onClick
  } = props;

  useState();

  const [itemsDivHeight, setItemsDivHeight] = useState(0);
  const categoryItemsRef = useRef<HTMLUListElement>(null);

  // Updating <ItemsContainer> height based on current <Items> height, in order to animate CSS transition.
  useLayoutEffect(() => {
    if (categoryItemsRef.current) {
      setItemsDivHeight(categoryItemsRef.current.clientHeight + 20);
    }
  }, [thematicsFilter, menuSelectedPath]);
  const selectedRootItem = menuSelectedPath !== undefined ? menuSelectedPath[0] : undefined;
  const isThematicsCategory = category.key == ROOT_THEMATICS_KEY;
  const isProductsCategory = category.key == ROOT_PRODUCTS_KEY;
  const items = category.hideChildren ? [] : category.children ?? [];
  const sortedItems = isProductsCategory ? sortItems(sorting, items) : items;
  const filteredItems = isThematicsCategory
    ? sortedItems.filter(item => thematicsFilter == '' || item.thematicFilters?.includes(thematicsFilter))
    : sortedItems;
  return (
    <StyledCategory>
      <StyledLink
        item={category}
        selected={selected}
        onClick={() => {
          setMenuSelectedPath([category]);
          onClick(category.label);
        }}
        closeMenu={closeMenu}
      >
        {category.label}
      </StyledLink>
      <StyledItemsContainer selected={selected} height={itemsDivHeight}>
        <StyledItems ref={categoryItemsRef}>
          <MenuSeeAllOption productsDisplayMode={productsDisplayMode} setProductsDisplayMode={setProductsDisplayMode} item={category} closeMenu={closeMenu} />
          {isProductsCategory && <MenuSorting setSorting={setSorting} sorting={sorting} />}
          {isThematicsCategory && (
            <MenuThematicsOptions parentRef={componentRef} item={category} thematicsFilter={thematicsFilter} setThematicsFilter={setThematicsFilter} />
          )}
          {filteredItems.map((item: MenuItem, index: number) => (
            <StyledItem key={index}>
              <MenuListItem
                onClick={onClick}
                withImages={false}
                withIcons={true}
                item={item}
                setMenuSelectedPath={setMenuSelectedPath}
                currentMenuPath={[selectedRootItem || item]}
                menuSelectedPath={menuSelectedPath}
                setProductsDisplayMode={setProductsDisplayMode}
                displayMode={DisplayMode.list}
                hidePatch={isThematicsCategory}
                displayProductsAmount={isThematicsCategory}
                closeMenu={closeMenu}
              />
            </StyledItem>
          ))}
        </StyledItems>
      </StyledItemsContainer>
    </StyledCategory>
  );
};

const StyledItemsContainer = styled.div<{ selected: boolean; height: number }>`
  height: ${props => (props.selected ? props.height : 0)}px;
  overflow: hidden;
  transition: all 0.5s;
`;
const StyledCategory = styled.li`
  margin: 0px;
  text-transform: capitalize;
  font-size: 16px;
`;
const StyledItems = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  //margin-left: 20px;
  //width: 300px;
  margin-top: 20px;

  > div,
  li {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const StyledItem = styled.li`
  font-size: 14px;
  text-transform: none;
`;
const StyledLink = styled(MenuLink)<{ selected: boolean }>`
  display: block;
  font-weight: 700;
  text-decoration: none;
  color: ${props => (props.selected ? '#ff0b75' : '#323232')};
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #ff0b75;
  }
`;
